import * as React from "react";

/* ══════════════════════════════════════════════════════════════════════════
   Sidebar state.

   The header used to open the mobile drawer with
   `window.dispatchEvent(new Event("sidebar:toggle"))` and the sidebar
   broadcast its collapsed state back the same way. That made the shell's
   state invisible to React: nothing could read "is the sidebar open?" to set
   `aria-expanded`, lock body scroll or trap focus.

   This is that state, in React. The window events are still emitted and
   listened for so any code outside the shell that used them keeps working.
   ══════════════════════════════════════════════════════════════════════════ */

const STORAGE_KEY = "merko_sidebar_collapsed";

interface SidebarContextValue {
    /** Desktop rail collapsed to icons only */
    collapsed: boolean;
    toggleCollapsed: () => void;
    setCollapsed: (value: boolean) => void;
    /** Mobile drawer open */
    mobileOpen: boolean;
    openMobile: () => void;
    closeMobile: () => void;
    toggleMobile: () => void;
    isDesktop: boolean;
}

const SidebarContext = React.createContext<SidebarContextValue | null>(null);

function readStoredCollapsed(): boolean {
    try {
        return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
        return false;
    }
}

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [collapsed, setCollapsedState] = React.useState<boolean>(readStoredCollapsed);
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const [isDesktop, setIsDesktop] = React.useState<boolean>(() =>
        typeof window !== "undefined" ? window.matchMedia("(min-width: 1024px)").matches : true
    );

    React.useEffect(() => {
        const mq = window.matchMedia("(min-width: 1024px)");
        const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
        if (mq.addEventListener) mq.addEventListener("change", handler);
        else mq.addListener(handler);
        setIsDesktop(mq.matches);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener("change", handler);
            else mq.removeListener(handler);
        };
    }, []);

    const setCollapsed = React.useCallback((value: boolean) => {
        setCollapsedState(value);
        try {
            localStorage.setItem(STORAGE_KEY, String(value));
        } catch {
            /* private mode — the preference just won't persist */
        }
    }, []);

    const toggleCollapsed = React.useCallback(
        () => setCollapsedState((prev) => {
            const next = !prev;
            try {
                localStorage.setItem(STORAGE_KEY, String(next));
            } catch {
                /* ignore */
            }
            return next;
        }),
        []
    );

    const openMobile = React.useCallback(() => setMobileOpen(true), []);
    const closeMobile = React.useCallback(() => setMobileOpen(false), []);
    const toggleMobile = React.useCallback(() => setMobileOpen((v) => !v), []);

    /* ── Back-compat bridge: honour the legacy window events ── */
    React.useEffect(() => {
        const open = () => setMobileOpen(true);
        const close = () => setMobileOpen(false);
        const toggle = () => setMobileOpen((v) => !v);
        window.addEventListener("sidebar:open", open);
        window.addEventListener("sidebar:close", close);
        window.addEventListener("sidebar:toggle", toggle);
        return () => {
            window.removeEventListener("sidebar:open", open);
            window.removeEventListener("sidebar:close", close);
            window.removeEventListener("sidebar:toggle", toggle);
        };
    }, []);

    /* ── Back-compat bridge: keep broadcasting the collapsed state ── */
    React.useEffect(() => {
        window.dispatchEvent(new CustomEvent("sidebar:state", { detail: { collapsed } }));
    }, [collapsed]);

    /* ── Lock the page behind the mobile drawer ── */
    React.useEffect(() => {
        if (isDesktop || !mobileOpen) return;
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previous;
        };
    }, [mobileOpen, isDesktop]);

    /* ── Escape closes the drawer ── */
    React.useEffect(() => {
        if (!mobileOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setMobileOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [mobileOpen]);

    const value = React.useMemo<SidebarContextValue>(
        () => ({
            collapsed,
            toggleCollapsed,
            setCollapsed,
            mobileOpen,
            openMobile,
            closeMobile,
            toggleMobile,
            isDesktop,
        }),
        [collapsed, toggleCollapsed, setCollapsed, mobileOpen, openMobile, closeMobile, toggleMobile, isDesktop]
    );

    return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
};

/**
 * Safe outside the provider: falls back to the window-event bridge so a
 * component rendered in isolation (tests, storybook) still behaves.
 */
export function useSidebar(): SidebarContextValue {
    const ctx = React.useContext(SidebarContext);
    const fallback = React.useMemo<SidebarContextValue>(
        () => ({
            collapsed: false,
            toggleCollapsed: () => window.dispatchEvent(new Event("sidebar:collapse-toggle")),
            setCollapsed: () => undefined,
            mobileOpen: false,
            openMobile: () => window.dispatchEvent(new Event("sidebar:open")),
            closeMobile: () => window.dispatchEvent(new Event("sidebar:close")),
            toggleMobile: () => window.dispatchEvent(new Event("sidebar:toggle")),
            isDesktop: true,
        }),
        []
    );
    return ctx ?? fallback;
}

export default SidebarProvider;
